import Swal from "sweetalert2";

// Centralized error handling for BRSR module
export const BrsrErrorHandler = {
  // Error codes mapping
  ERROR_CODES: {
    INVALID_REPORT_ID: "INVALID_REPORT_ID",
    NOT_FOUND: "NOT_FOUND",
    VALIDATION_ERROR: "VALIDATION_ERROR",
    BLOB_ERROR: "BLOB_ERROR",
    UNAUTHORIZED: "UNAUTHORIZED",
    FORBIDDEN: "FORBIDDEN",
    INTERNAL_ERROR: "INTERNAL_ERROR",
    CONCURRENT_EDIT: "CONCURRENT_EDIT",
    FILE_UPLOAD_ERROR: "FILE_UPLOAD_ERROR",
    NETWORK_ERROR: "NETWORK_ERROR",
  },

  // Standard error messages
  ERROR_MESSAGES: {
    INVALID_REPORT_ID: "Invalid report ID format. Must be a valid GUID.",
    NOT_FOUND: "Report not found",
    VALIDATION_ERROR: "{field} is required",
    BLOB_ERROR: "Failed to upload report data",
    UNAUTHORIZED: "Access denied. Please log in.",
    FORBIDDEN: "Cannot access reports from another company",
    INTERNAL_ERROR: "An unexpected error occurred",
    CONCURRENT_EDIT: "Report was modified by another user",
    FILE_UPLOAD_ERROR: "Failed to upload file",
    NETWORK_ERROR: "Network error. Please check your connection.",
  },

  // Show error alert with SweetAlert2
  showError: (errorCode, customMessage = null) => {
    const message = customMessage || BrsrErrorHandler.ERROR_MESSAGES[errorCode] || "An error occurred";
    
    Swal.fire({
      icon: "error",
      width: 400,
      heightAuto: false,
      position: "center",
      html: `<h4 style="color:#CC2229;font-size: 20px;">Error!</h4><p style="font-size:16px;">${message}</p>`,
      iconHtml: '<img src="/assets/img/error.png" style="width: 55px; height: 55px;" />',
      confirmButtonText: "Close",
      scrollbarPadding: false,
    });
  },

  // Show success alert
  showSuccess: (title = "Success", message = "Operation completed successfully") => {
    Swal.fire({
      icon: "success",
      width: 400,
      heightAuto: false,
      position: "center",
      html: `<h4 style="color:#407775;font-size: 20px;">${title}</h4><p style="font-size:16px;">${message}</p>`,
      iconHtml: '<img src="/assets/img/successicon.png" style="width: 55px; height: 55px;" />',
      confirmButtonText: "Continue",
      scrollbarPadding: false,
    });
  },

  // Show warning alert
  showWarning: (title = "Warning", message = "Please review") => {
    return Swal.fire({
      icon: "warning",
      width: 400,
      heightAuto: false,
      position: "center",
      html: `<h4 style="color:#F39C12;font-size: 20px;">${title}</h4><p style="font-size:16px;">${message}</p>`,
      confirmButtonText: "OK",
      scrollbarPadding: false,
    });
  },

  // Handle API errors with retry
  handleApiError: async (error, retryFn = null, maxRetries = 3) => {
    console.error("BRSR API Error:", error);

    if (!error.response) {
      BrsrErrorHandler.showError("NETWORK_ERROR");
      return false;
    }

    const status = error.response.status;
    const errorData = error.response.data || {};

    switch (status) {
      case 400:
        BrsrErrorHandler.showError(
          "VALIDATION_ERROR",
          errorData.message || "Invalid request data"
        );
        break;
      case 401:
        BrsrErrorHandler.showError("UNAUTHORIZED");
        break;
      case 403:
        BrsrErrorHandler.showError("FORBIDDEN");
        break;
      case 404:
        BrsrErrorHandler.showError("NOT_FOUND");
        break;
      case 409:
        BrsrErrorHandler.showError("CONCURRENT_EDIT");
        break;
      case 500:
        BrsrErrorHandler.showError(
          "INTERNAL_ERROR",
          errorData.message || "Server error occurred"
        );
        break;
      default:
        BrsrErrorHandler.showError(
          "INTERNAL_ERROR",
          errorData.message || "Unknown error occurred"
        );
    }

    return false;
  },

  // Validate GUID format
  isValidGuid: (guid) => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  },

  // Log error for debugging
  logError: (errorCode, context = {}) => {
    const errorLog = {
      timestamp: new Date().toISOString(),
      code: errorCode,
      message: BrsrErrorHandler.ERROR_MESSAGES[errorCode],
      context,
    };
    console.error("[BRSR Error Log]", errorLog);
    // In production, this could be sent to an error tracking service
  },
};

export default BrsrErrorHandler;
